// src/money.ts
var SCALE = 6;
var FACTOR = 1000000n;
function toMicros(value) {
  if (typeof value === "bigint") return value;
  const raw = typeof value === "number" ? numberToDecimalString(value) : value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(raw)) throw new Error(`not a decimal amount: ${JSON.stringify(value)}`);
  const negative = raw.startsWith("-");
  const [intPart = "0", fracPart = ""] = (negative ? raw.slice(1) : raw).split(".");
  const kept = fracPart.slice(0, SCALE).padEnd(SCALE, "0");
  const nextDigit = fracPart.charCodeAt(SCALE) - 48;
  let micros = BigInt(intPart) * FACTOR + BigInt(kept);
  if (nextDigit >= 5 && nextDigit <= 9) micros += 1n;
  return negative ? -micros : micros;
}
function numberToDecimalString(n) {
  if (!Number.isFinite(n)) throw new Error(`not a finite amount: ${n}`);
  return n.toFixed(SCALE);
}
function fromMicros(micros, dp = 2) {
  if (dp > SCALE) throw new Error(`cannot render more than ${SCALE} decimal places`);
  const negative = micros < 0n;
  let abs = negative ? -micros : micros;
  const drop = BigInt(SCALE - dp);
  if (drop > 0n) {
    const divisor = 10n ** drop;
    const remainder = abs % divisor;
    abs = abs / divisor;
    if (remainder * 2n >= divisor) abs += 1n;
  }
  const unit = 10n ** BigInt(dp);
  const whole = abs / unit;
  const frac = abs % unit;
  const body = dp === 0 ? `${whole}` : `${whole}.${frac.toString().padStart(dp, "0")}`;
  return negative && abs !== 0n ? `-${body}` : body;
}
var add = (a, b) => a + b;
var sub = (a, b) => a - b;
var sum = (values) => values.reduce((acc, v) => acc + v, 0n);
var isNegative = (v) => v < 0n;
function percentOf(micros, percent) {
  const rate = toMicros(percent);
  const negative = micros < 0n !== rate < 0n;
  const abs = (micros < 0n ? -micros : micros) * (rate < 0n ? -rate : rate);
  const divisor = FACTOR * 100n;
  const whole = abs / divisor;
  const rounded = abs % divisor === 0n ? whole : whole + (abs % divisor >= divisor / 2n ? 1n : 0n);
  return negative ? -rounded : rounded;
}

// src/commission.ts
var DEFAULT_COMMISSION_LADDER = [
  { thresholdAmount: "0", ratePercent: "16" },
  { thresholdAmount: "200000", ratePercent: "15" },
  { thresholdAmount: "300000", ratePercent: "14" },
  { thresholdAmount: "400000", ratePercent: "13" },
  { thresholdAmount: "500000", ratePercent: "12" }
];
function rateForCumulative(spendSoFarMicros, tiers) {
  const applicable = [...tiers].sort((a, b) => toMicros(a.thresholdAmount) < toMicros(b.thresholdAmount) ? -1 : 1).filter((tier) => spendSoFarMicros >= toMicros(tier.thresholdAmount));
  const chosen = applicable.at(-1) ?? [...tiers][0];
  if (!chosen) throw new Error("commission ladder is empty");
  return chosen.ratePercent;
}
function computeCommission(grossAmount, spendSoFar, tiers) {
  const grossMicros = toMicros(grossAmount);
  if (grossMicros <= 0n) throw new Error("top-up amount must be positive");
  const cumulativeMicros = toMicros(spendSoFar);
  const ratePercent = rateForCumulative(cumulativeMicros, tiers);
  const commissionMicros = percentOf(grossMicros, ratePercent);
  return {
    ratePercent,
    grossAmount: fromMicros(grossMicros, 2),
    commissionAmount: fromMicros(commissionMicros, 2),
    netAmount: fromMicros(grossMicros - commissionMicros, 2),
    cumulativeBefore: fromMicros(cumulativeMicros, 2)
  };
}
var TRANSACTION_TYPES = ["deposit", "refund", "allocation", "return"];
var TRANSACTION_TYPE_LABELS = {
  deposit: "\u041F\u043E\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u0435",
  refund: "\u0412\u043E\u0437\u0432\u0440\u0430\u0442",
  allocation: "\u041F\u043E\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u0435 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430",
  return: "\u0412\u043E\u0437\u0432\u0440\u0430\u0442 \u0441 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430"
};
var isInternalTransfer = (type) => type === "allocation" || type === "return";
function computeAllocation(amount, ratePercent) {
  const amountMicros = toMicros(amount);
  if (amountMicros <= 0n) throw new Error("allocation amount must be positive");
  const rateMicros = toMicros(ratePercent);
  const limit = toMicros("100");
  if (rateMicros < -limit || rateMicros > limit) throw new Error("rate must be between -100 and 100");
  const commissionMicros = percentOf(amountMicros, ratePercent);
  return {
    ratePercent: fromMicros(rateMicros, 2),
    grossAmount: fromMicros(amountMicros, 2),
    commissionAmount: fromMicros(commissionMicros, 2),
    netAmount: fromMicros(amountMicros - commissionMicros, 2),
    cumulativeBefore: "0.00"
  };
}
function accountLeg(amount) {
  const micros = toMicros(amount);
  return {
    ratePercent: "0.00",
    grossAmount: fromMicros(micros, 2),
    commissionAmount: "0.00",
    netAmount: fromMicros(micros, 2),
    cumulativeBefore: "0.00"
  };
}
function computeReturn(amount, ratePercent) {
  const amountMicros = toMicros(amount);
  if (amountMicros <= 0n) throw new Error("return amount must be positive");
  const rateMicros = toMicros(ratePercent);
  const hundred = toMicros("100");
  if (rateMicros >= hundred) throw new Error("rate must be below 100");
  if (rateMicros <= -hundred) throw new Error("rate must be above -100");
  const creditMicros = toMicros(fromMicros(amountMicros * hundred / (hundred - rateMicros), 2));
  const feeMicros = creditMicros - amountMicros;
  return {
    ratePercent: fromMicros(rateMicros, 2),
    grossAmount: fromMicros(-creditMicros, 2),
    commissionAmount: fromMicros(-feeMicros, 2),
    netAmount: fromMicros(-amountMicros, 2),
    cumulativeBefore: "0.00"
  };
}

// src/notifications.ts
var NOTIFICATION_KINDS = [
  "accountLinked",
  "deposit",
  "allocation",
  "return",
  "refund",
  "summaryMorning",
  "summaryEvening",
  "summaryManual",
  "lowBalanceAccount",
  "lowBalanceCabinet",
  "broadcast"
];
var isNotificationKind = (v) => NOTIFICATION_KINDS.includes(v);
function notificationAllowed(prefs, kind) {
  return prefs?.[kind] !== false;
}
var BY_TRANSACTION = {
  deposit: "deposit",
  refund: "refund",
  allocation: "allocation",
  return: "return"
};
var notificationKindForTransaction = (type) => BY_TRANSACTION[type];
var NOTIFICATION_LABELS = {
  accountLinked: {
    label: "\u041F\u0440\u0438\u0432\u044F\u0437\u043A\u0430 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430",
    description: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442 \u043F\u0440\u0438\u0432\u044F\u0437\u0430\u043D \u043A \u043A\u043B\u0438\u0435\u043D\u0442\u0443 \u2014 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435, id \u0438 \u043F\u0440\u0438\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435 \u043E\u0442 \u043F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u044B"
  },
  deposit: { label: "\u041F\u043E\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u0435 \u0441\u0447\u0451\u0442\u0430", description: "\u041A\u043B\u0438\u0435\u043D\u0442 \u0437\u0430\u0432\u0451\u043B \u0434\u0435\u043D\u044C\u0433\u0438: \u0441\u0443\u043C\u043C\u0430, \u043A\u043E\u043C\u0438\u0441\u0441\u0438\u044F, \u043E\u0441\u0442\u0430\u0442\u043E\u043A" },
  allocation: { label: "\u041F\u043E\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u0435 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430", description: "\u0414\u0435\u043D\u044C\u0433\u0438 \u0441\u043E \u0441\u0447\u0451\u0442\u0430 \u043F\u0435\u0440\u0435\u0432\u0435\u0434\u0435\u043D\u044B \u043D\u0430 \u043A\u0430\u0431\u0438\u043D\u0435\u0442" },
  return: { label: "\u0412\u043E\u0437\u0432\u0440\u0430\u0442 \u0441 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430", description: "\u041E\u0441\u0442\u0430\u0442\u043E\u043A \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430 \u0432\u0435\u0440\u043D\u0443\u043B\u0441\u044F \u043D\u0430 \u0441\u0447\u0451\u0442 \u043A\u043B\u0438\u0435\u043D\u0442\u0430" },
  refund: { label: "\u0412\u043E\u0437\u0432\u0440\u0430\u0442 \u0441\u0440\u0435\u0434\u0441\u0442\u0432", description: "\u0414\u0435\u043D\u044C\u0433\u0438 \u0432\u044B\u0432\u0435\u0434\u0435\u043D\u044B \u043A\u043B\u0438\u0435\u043D\u0442\u0443 \u0441\u043E \u0441\u0447\u0451\u0442\u0430" },
  summaryMorning: { label: "\u0421\u0432\u043E\u0434\u043A\u0430 \u0443\u0442\u0440\u043E\u043C", description: "\u0421\u043F\u0435\u043D\u0434 \u0438 \u043E\u0441\u0442\u0430\u0442\u043A\u0438 \u043F\u043E \u0440\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044E \u0443\u0442\u0440\u0435\u043D\u043D\u0435\u0439 \u0441\u0432\u043E\u0434\u043A\u0438" },
  summaryEvening: { label: "\u0421\u0432\u043E\u0434\u043A\u0430 \u0432\u0435\u0447\u0435\u0440\u043E\u043C", description: "\u0421\u043F\u0435\u043D\u0434 \u0438 \u043E\u0441\u0442\u0430\u0442\u043A\u0438 \u043F\u043E \u0440\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044E \u0432\u0435\u0447\u0435\u0440\u043D\u0435\u0439 \u0441\u0432\u043E\u0434\u043A\u0438" },
  summaryManual: {
    label: "\u0421\u0432\u043E\u0434\u043A\u0430 \u043F\u043E \u043A\u043D\u043E\u043F\u043A\u0435",
    description: "\u041A\u043E\u0433\u0434\u0430 \u0430\u0434\u043C\u0438\u043D \u0436\u043C\u0451\u0442 \xAB\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C \u0441\u0432\u043E\u0434\u043A\u0443\xBB \u0432 \u043A\u0430\u0440\u0442\u043E\u0447\u043A\u0435 \u043A\u043B\u0438\u0435\u043D\u0442\u0430"
  },
  lowBalanceAccount: {
    label: "\u0411\u0430\u043B\u0430\u043D\u0441 \u0441\u0447\u0451\u0442\u0430 \u0437\u0430\u043A\u0430\u043D\u0447\u0438\u0432\u0430\u0435\u0442\u0441\u044F",
    description: "\u041E\u0441\u0442\u0430\u0442\u043E\u043A \u0441\u0447\u0451\u0442\u0430 \u0443\u043F\u0430\u043B \u043D\u0438\u0436\u0435 \u043F\u043E\u0440\u043E\u0433\u0430 \u0438\u043B\u0438 \u043A\u043E\u043D\u0447\u0438\u043B\u0441\u044F"
  },
  lowBalanceCabinet: {
    label: "\u0411\u0430\u043B\u0430\u043D\u0441 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430 \u0437\u0430\u043A\u0430\u043D\u0447\u0438\u0432\u0430\u0435\u0442\u0441\u044F",
    description: "\u041E\u0442\u0434\u0435\u043B\u044C\u043D\u043E \u043F\u043E \u043A\u0430\u0436\u0434\u043E\u043C\u0443 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0443 \u2014 \u0442\u0430\u043C, \u0433\u0434\u0435 \u0434\u0435\u043D\u044C\u0433\u0438 \u043B\u0435\u0436\u0430\u0442 \u043D\u0430 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0435"
  },
  broadcast: { label: "\u0420\u0430\u0441\u0441\u044B\u043B\u043A\u0438", description: "\u0420\u0443\u0447\u043D\u044B\u0435 \u0440\u0430\u0441\u0441\u044B\u043B\u043A\u0438 \u0438\u0437 \u0434\u0430\u0448\u0431\u043E\u0440\u0434\u0430" }
};
var NOTIFICATION_GROUPS = [
  { label: "\u041E\u043F\u0435\u0440\u0430\u0446\u0438\u0438", kinds: ["accountLinked", "deposit", "allocation", "return", "refund"] },
  { label: "\u0421\u0432\u043E\u0434\u043A\u0438", kinds: ["summaryMorning", "summaryEvening", "summaryManual"] },
  { label: "\u041F\u0440\u0435\u0434\u0443\u043F\u0440\u0435\u0436\u0434\u0435\u043D\u0438\u044F", kinds: ["lowBalanceAccount", "lowBalanceCabinet"] },
  { label: "\u041F\u0440\u043E\u0447\u0435\u0435", kinds: ["broadcast"] }
];

// src/contracts.ts
import * as z from "zod";

// src/trackers.ts
var APP_TRACKERS = [
  "ADBRIX_V2",
  "ADJUST",
  "AIRBRIDGE",
  "APPSFLYER",
  "BRANCH",
  "KOCHAVA",
  "SINGULAR",
  "TENJIN"
];
var isAppTracker = (value) => APP_TRACKERS.includes(value);
var MAX_APPS_PER_REQUEST = 100;
var APP_REQUEST_STATUSES = ["new", "in_progress", "app_added", "app_declined"];
var APP_REQUEST_STATUS_LABELS = {
  new: "\u041D\u043E\u0432\u0430\u044F",
  in_progress: "\u0412 \u0440\u0430\u0431\u043E\u0442\u0435",
  app_added: "\u0414\u043E\u0431\u0430\u0432\u043B\u0435\u043D\u043E",
  app_declined: "\u041E\u0442\u043A\u043B\u043E\u043D\u0435\u043D\u043E"
};

// src/providers.ts
var AD_PROVIDERS = ["moloco", "facebook", "tiktok", "unity", "outbrain", "taboola"];
var AD_PROVIDER_LABELS = {
  moloco: "Moloco",
  facebook: "Facebook",
  tiktok: "TikTok",
  unity: "Unity Ads",
  outbrain: "Outbrain",
  taboola: "Taboola"
};
var MANUAL_PROVIDERS = ["outbrain", "taboola"];
var isManualProvider = (provider) => MANUAL_PROVIDERS.includes(provider);
var canBlockCabinet = (provider) => isManualProvider(provider);
var isAdProvider = (value) => AD_PROVIDERS.includes(value);
var MASTER_BALANCE_PROVIDERS = ["moloco"];
var hasMasterBalance = (provider) => MASTER_BALANCE_PROVIDERS.includes(provider);
var accountLabel = (provider) => {
  const name = AD_PROVIDER_LABELS[provider] ?? provider;
  return isAdProvider(provider) && hasMasterBalance(provider) ? `\u0411\u0430\u043B\u0430\u043D\u0441 ${name}` : `\u0421\u0447\u0451\u0442 ${name}`;
};
var usesCabinetBalance = (provider) => !hasMasterBalance(provider);
var ZERO_DECIMAL_CURRENCIES = /* @__PURE__ */ new Set(["JPY", "KRW", "VND", "CLP", "ISK", "PYG", "UGX", "XOF", "XAF"]);
function minorUnitsToMicros(value, currency) {
  if (value === null || value === void 0 || value === "") return null;
  const raw = BigInt(String(value).split(".")[0] ?? "0");
  return ZERO_DECIMAL_CURRENCIES.has(currency) ? raw * 1000000n : raw * 10000n;
}

// src/contracts.ts
var moneyString = z.string().regex(/^-?\d+(\.\d+)?$/, "must be a decimal amount").describe("Decimal amount as a string; never a float.");
var apiSuccess = (data) => z.object({ success: z.literal(true), data });
var apiErrorBody = z.object({
  success: z.literal(false),
  data: z.object({ code: z.number(), error: z.string(), type: z.string() })
});
var commissionTierSchema = z.object({
  thresholdAmount: moneyString,
  ratePercent: moneyString
});
var notificationPrefsSchema = z.object(
  Object.fromEntries(NOTIFICATION_KINDS.map((k) => [k, z.boolean().optional()]))
);
var clientSchema = z.object({
  id: z.number(),
  telegramChatId: z.string(),
  title: z.string(),
  chatType: z.string(),
  status: z.enum(["active", "paused", "archived"]),
  /**
   * Off means the client's own chat hears nothing from us: summaries, balance
   * warnings, deposit confirmations and broadcasts all stop. Our side is
   * untouched -- the admin chat still gets its warnings, and the bot still
   * answers when the client asks it something.
   */
  notificationsEnabled: z.boolean(),
  /**
   * The per-kind switches under the master above. Sparse: only the kinds the
   * client has been opted out of are present, so an absent key means on.
   */
  notificationPrefs: notificationPrefsSchema,
  createdAt: z.string()
});
var clientOverviewSchema = clientSchema.extend({
  accountCount: z.number(),
  toppedUpGross: moneyString,
  commissionEarned: moneyString,
  netFunded: moneyString,
  totalSpend: moneyString,
  availableBalance: moneyString,
  ratePercent: moneyString
});
var adAccountSchema = z.object({
  id: z.number(),
  provider: z.string(),
  externalId: z.string(),
  title: z.string(),
  timezone: z.string(),
  currency: z.string(),
  clientId: z.number().nullable(),
  clientTitle: z.string().nullable(),
  isTracked: z.boolean(),
  /** Set when the platform blocked the cabinet; its remainder went back then. */
  blockedAt: z.string().nullable(),
  syncedAt: z.string().nullable(),
  /** Set when the worker switched collection off itself; null when an admin did. */
  autoDisabledAt: z.string().nullable(),
  /** Why collection is off: the platform's refusal, or "Выключен вручную". */
  disableReason: z.string().nullable(),
  totalSpend: moneyString
});
var topUpSchema = z.object({
  id: z.number(),
  clientId: z.number(),
  /** Signed amounts follow from it: a refund's gross, commission and net are negative. */
  type: z.enum(TRANSACTION_TYPES),
  /** The platform this row moves money on; without a cabinet it is that platform's account. */
  provider: z.enum(AD_PROVIDERS),
  /** Set on the cabinet side of a transfer between the account and a cabinet. */
  adAccountId: z.number().nullable(),
  /** Both sides of one transfer share it; they are reversed together. */
  groupId: z.string().nullable(),
  grossAmount: moneyString,
  ratePercent: moneyString,
  commissionAmount: moneyString,
  netAmount: moneyString,
  cumulativeBefore: moneyString,
  paymentMethod: z.enum(["crypto", "fiat"]),
  network: z.string().nullable(),
  token: z.string().nullable(),
  txHash: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string()
});
var topUpWithClientSchema = topUpSchema.extend({ clientTitle: z.string() });
var dailySpendPointSchema = z.object({
  date: z.string(),
  spend: moneyString,
  impressions: z.number(),
  clicks: z.number(),
  isFinal: z.boolean()
});
var moneyByProvider = z.record(z.string(), moneyString);
var providerSpendSchema = z.object({
  /** Spend inside the window the dashboard has picked. */
  range: moneyString,
  today: moneyString,
  yesterday: moneyString,
  week: moneyString,
  month: moneyString,
  total: moneyString,
  accountsSpendingToday: z.number()
});
var statsSchema = z.object({
  rangeSpend: moneyString,
  rangeAccounts: z.number(),
  byProvider: z.record(z.string(), providerSpendSchema),
  spendTodayByProvider: moneyByProvider,
  spendYesterdayByProvider: moneyByProvider,
  totalSpendByProvider: moneyByProvider,
  clientCount: z.number(),
  accountCount: z.number(),
  trackedAccountCount: z.number(),
  unassignedAccountCount: z.number(),
  accountsSpendingToday: z.number(),
  commissionEarned: moneyString,
  totalSpend: moneyString,
  totalFunded: moneyString,
  totalFundedGross: moneyString,
  outstandingBalance: moneyString,
  spendToday: moneyString,
  spendYesterday: moneyString,
  spend7d: moneyString,
  spendMonth: moneyString,
  spendAvg7d: moneyString,
  impressionsToday: z.number(),
  clicksToday: z.number(),
  lastSyncedAt: z.string().nullable()
});
var topAccountSchema = z.object({
  id: z.number(),
  title: z.string(),
  clientTitle: z.string().nullable(),
  spendToday: moneyString,
  totalSpend: moneyString
});
var createClientBody = z.object({
  telegramChatId: z.string().min(1),
  title: z.string().min(1).max(200)
});
var updateClientBody = z.object({
  notificationsEnabled: z.boolean().optional(),
  /**
   * Merged into what is stored rather than replacing it, so a caller that
   * knows about fewer kinds than exist cannot switch the rest back on.
   */
  notificationPrefs: notificationPrefsSchema.optional()
}).refine((b) => Object.values(b).some((v) => v !== void 0), "nothing to change");
var createTopUpBody = z.object({
  type: z.enum(["deposit", "refund"]).default("deposit"),
  /**
   * The platform the money is for. Moloco is charged the ladder commission
   * and lands on its master balance; the others land on that platform's
   * account free of commission and are charged when moved onto a cabinet.
   */
  provider: z.enum(AD_PROVIDERS).default("moloco"),
  /** Deposit: what the client paid. Refund: what comes off their balance. Always positive. */
  grossAmount: moneyString.refine((v) => Number(v) > 0, "must be positive"),
  /**
   * Refund only, signed: +16 returns commission on top of the amount, -16
   * keeps a fee out of it, omitted or "0" is a refund without commission.
   * Deposits ignore it -- their rate comes from the ladder.
   */
  refundRatePercent: moneyString.optional(),
  paymentMethod: z.enum(["crypto", "fiat"]).default("crypto"),
  network: z.string().max(32).optional(),
  token: z.string().max(32).optional(),
  // Not validated per-chain: hash formats differ enough that a strict rule
  // would reject valid deposits, and a wrong hash is visible in the link.
  txHash: z.string().max(128).optional(),
  note: z.string().max(2e3).optional()
}).transform(
  (body) => (
    // Chain details are meaningless on a fiat deposit; drop them rather than
    // storing a network against money that never touched one.
    body.paymentMethod === "fiat" ? { ...body, network: void 0, token: void 0, txHash: void 0 } : body
  )
);
var editTopUpBody = z.object({
  /**
   * `deposit` <-> `refund`, or `allocation` <-> `return`. Crossing between
   * the two is refused: a deposit is one row and a transfer is two, and the
   * transfer needs a cabinet the deposit never named.
   */
  type: z.enum(TRANSACTION_TYPES).optional(),
  /**
   * Positive, and read the way the booking form reads it: a deposit's gross,
   * the amount a refund or a return takes off a balance, the debit an
   * allocation makes against the account. Every sign follows from the type.
   */
  amount: moneyString.refine((v) => Number(v) > 0, "must be positive").optional(),
  /** Signed, as on the booking forms. Kept as it is when omitted -- never re-read from the ladder. */
  ratePercent: moneyString.optional(),
  /** A date ("2026-09-24") or a full ISO timestamp. Both legs of a transfer move together. */
  occurredAt: z.string().min(4).max(40).optional(),
  /** `null` clears it. */
  note: z.string().max(2e3).nullish(),
  /** Deposits and refunds only; a transfer takes its platform from the cabinet. */
  provider: z.enum(AD_PROVIDERS).optional(),
  paymentMethod: z.enum(["crypto", "fiat"]).optional(),
  network: z.string().max(32).nullish(),
  token: z.string().max(32).nullish(),
  txHash: z.string().max(128).nullish(),
  /** Transfers only: re-point the pair at another of this client's cabinets. */
  adAccountId: z.number().int().positive().optional()
}).transform(
  (body) => (
    // Same reason the booking form drops them: chain details are meaningless
    // on fiat money, and a correction to fiat should clear what was there.
    body.paymentMethod === "fiat" ? { ...body, network: null, token: null, txHash: null } : body
  )
);
var editTopUpResultSchema = z.object({
  rows: z.array(topUpSchema),
  /** Change to the platform account's balance. */
  accountDelta: moneyString,
  /** Change to the cabinet's balance; "0.00" for a deposit or a refund. */
  cabinetDelta: moneyString,
  /** Change to what we earned on this transaction. */
  commissionDelta: moneyString
});
var allocateBody = z.object({
  adAccountId: z.number().int().positive(),
  /** Taken off the account in full; the cabinet gets it minus the commission. */
  amount: moneyString.refine((v) => Number(v) > 0, "must be positive"),
  /** Prefilled from the ladder in the dashboard, but the admin's to set. */
  ratePercent: moneyString.optional(),
  note: z.string().max(2e3).optional()
});
var returnToAccountBody = z.object({
  /** Defaults to everything the cabinet has left. */
  amount: moneyString.refine((v) => Number(v) > 0, "must be positive").optional(),
  /** Defaults to the rate of the last funding, which is what it gives back. */
  ratePercent: moneyString.optional(),
  note: z.string().max(2e3).optional()
});
var cabinetBalanceSchema = z.object({
  adAccountId: z.number(),
  clientId: z.number(),
  clientTitle: z.string(),
  provider: z.enum(AD_PROVIDERS),
  title: z.string(),
  externalId: z.string(),
  currency: z.string(),
  isTracked: z.boolean(),
  blockedAt: z.string().nullable(),
  autoDisabledAt: z.string().nullable(),
  disableReason: z.string().nullable(),
  syncedAt: z.string().nullable(),
  netFunded: moneyString,
  spendToday: moneyString,
  totalSpend: moneyString,
  availableBalance: moneyString,
  lastRatePercent: moneyString.nullable(),
  lastFundedAt: z.string().nullable(),
  clientStatus: z.string(),
  lowBalanceNotifiedAt: z.string().nullable(),
  strandedNotifiedAt: z.string().nullable()
});
var updateTiersBody = z.object({
  tiers: z.array(commissionTierSchema).min(1)
});
var assignAccountBody = z.object({
  clientId: z.number().nullable()
});
var trackAccountBody = z.object({
  isTracked: z.boolean()
});
var loginHandle = z.string().min(3).max(160);
var loginBody = z.object({
  email: loginHandle,
  password: z.string().min(8)
});
var roleSchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  permissions: z.array(z.string()),
  isSystem: z.boolean(),
  userCount: z.number(),
  createdAt: z.string()
});
var adminUserSchema = z.object({
  id: z.number(),
  email: z.string(),
  name: z.string().nullable(),
  isActive: z.boolean(),
  roleId: z.number().nullable(),
  roleName: z.string().nullable(),
  roleSlug: z.string().nullable(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string()
});
var createRoleBody = z.object({
  name: z.string().min(2).max(80),
  permissions: z.array(z.string()).default([])
});
var updateRoleBody = z.object({
  name: z.string().min(2).max(80).optional(),
  permissions: z.array(z.string()).optional()
});
var createUserBody = z.object({
  email: loginHandle,
  password: z.string().min(8).max(200),
  name: z.string().max(120).optional(),
  roleId: z.number().int().positive()
});
var updateUserBody = z.object({
  password: z.string().min(8).max(200).optional(),
  name: z.string().max(120).optional(),
  roleId: z.number().int().positive().optional(),
  isActive: z.boolean().optional()
});
var settingsSchema = z.object({
  syncCron: z.string(),
  discoverCron: z.string(),
  campaignCron: z.string(),
  facebookCron: z.string(),
  tiktokCron: z.string(),
  unityCron: z.string(),
  walletsCron: z.string(),
  summaryCronMorning: z.string(),
  summaryCronEvening: z.string(),
  timezone: z.string(),
  trailingWindowDays: z.number(),
  lowBalanceThreshold: moneyString,
  /** Consecutive permission failures before a cabinet switches itself off; 0 is never. */
  autoDisableAfterErrors: z.number(),
  ignoredChatIds: z.array(z.string().regex(/^-?\d{1,20}$/, "chat_id must be a number"))
});
var appRequestSchema = z.object({
  id: z.number(),
  clientId: z.number(),
  clientTitle: z.string(),
  adAccountId: z.number(),
  adAccountTitle: z.string(),
  adAccountExternalId: z.string(),
  appUrl: z.string(),
  tracker: z.enum(APP_TRACKERS),
  status: z.enum(APP_REQUEST_STATUSES),
  requestedByName: z.string().nullable(),
  requestedByTelegramId: z.string().nullable(),
  createdAt: z.string()
});
var appRequestItem = z.object({
  appUrl: z.string().trim().url().max(2e3),
  tracker: z.enum(APP_TRACKERS)
});
var createAppRequestBody = z.object({
  adAccountId: z.number().int().positive(),
  apps: z.array(appRequestItem).min(1).max(MAX_APPS_PER_REQUEST)
});
var miniAppContextSchema = z.object({
  clientId: z.number(),
  clientTitle: z.string(),
  accounts: z.array(
    z.object({
      id: z.number(),
      title: z.string(),
      externalId: z.string()
    })
  )
});
var updateAppRequestStatusBody = z.object({
  status: z.enum(APP_REQUEST_STATUSES)
});
var campaignSchema = z.object({
  id: z.number(),
  provider: z.string(),
  externalId: z.string(),
  adAccountId: z.number(),
  adAccountTitle: z.string(),
  clientId: z.number().nullable(),
  clientTitle: z.string().nullable(),
  title: z.string(),
  state: z.string().nullable(),
  enablingState: z.string().nullable(),
  type: z.string().nullable(),
  deviceOs: z.string().nullable(),
  countries: z.array(z.string()),
  currency: z.string(),
  trackingCompany: z.string().nullable(),
  goalType: z.string().nullable(),
  /** As stated by Moloco, in micros, over `budgetPeriod`. */
  budgetMicros: z.string().nullable(),
  budgetPeriod: z.string().nullable(),
  /** The same cap expressed per day, which is what totals are built from. */
  dailyBudget: moneyString.nullable(),
  spendToday: moneyString,
  spend7d: moneyString,
  totalSpend: moneyString,
  startsAt: z.string().nullable(),
  syncedAt: z.string().nullable()
});
var budgetSummarySchema = z.object({
  campaignCount: z.number(),
  liveCampaignCount: z.number(),
  /** Sum of the daily caps of campaigns that can actually spend right now. */
  dailyBudgetLive: moneyString,
  /** Every campaign's cap, live or not -- the ceiling if everything resumed. */
  dailyBudgetAll: moneyString,
  spendToday: moneyString
});
var accountBudgetSchema = budgetSummarySchema.omit({ spendToday: true }).extend({ adAccountId: z.number() });
var campaignDailyPointSchema = z.object({
  date: z.string(),
  spend: moneyString,
  impressions: z.number(),
  clicks: z.number(),
  dailyBudget: moneyString.nullable(),
  state: z.string().nullable()
});
var depositWalletSchema = z.object({
  id: z.number(),
  network: z.string(),
  token: z.string(),
  address: z.string(),
  memo: z.string().nullable(),
  note: z.string().nullable(),
  isActive: z.boolean(),
  /** Platforms whose bot offers this address in /deposit. */
  providers: z.array(z.string()),
  createdAt: z.string()
});
var createDepositWalletBody = z.object({
  network: z.string().min(1).max(40),
  token: z.string().min(1).max(20),
  address: z.string().trim().min(4).max(200),
  memo: z.string().trim().max(120).optional(),
  note: z.string().trim().max(200).optional(),
  isActive: z.boolean().optional(),
  providers: z.array(z.enum(AD_PROVIDERS)).min(1).optional(),
  /**
   * Confirms moving platforms off other active wallets for the same network
   * and token. Without it an overlap is refused.
   */
  takeOver: z.boolean().optional()
});
var updateDepositWalletBody = createDepositWalletBody.partial();
var adCredentialSchema = z.object({
  id: z.number(),
  provider: z.string(),
  label: z.string(),
  status: z.string(),
  lastAuthAt: z.string().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string()
});
var createCredentialBody = z.object({
  provider: z.enum(AD_PROVIDERS).default("moloco"),
  label: z.string().min(1).max(120),
  /** The token itself. Optional for TikTok, which can exchange for one. */
  apiKey: z.string().min(10).max(4e3).optional(),
  /**
   * TikTok's one-time authorisation. Given these, the server trades them for a
   * long-lived access token and stores that -- the app secret is not a token,
   * however similar the two look.
   */
  appId: z.string().regex(/^\d{1,30}$/).optional(),
  secret: z.string().min(10).max(200).optional(),
  authCode: z.string().min(4).max(500).optional(),
  /** Unity: the key id and organization that pair with the secret. */
  keyId: z.string().max(120).optional(),
  organizationId: z.string().regex(/^\d{1,30}$/).optional(),
  /**
   * TikTok only: the advertiser ids this token may read. Its endpoint for
   * listing them wants app credentials rather than an access token, so they
   * have to be given rather than discovered.
   */
  advertiserIds: z.array(z.string().regex(/^\d{1,30}$/)).max(200).optional()
});
var spendPointSchema = z.looseObject({
  date: z.string(),
  spend: moneyString
});
var spendSeriesSchema = z.object({
  providers: z.array(z.string()),
  points: z.array(spendPointSchema)
});

// src/schedule.ts
var clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, Math.trunc(n)));
function scheduleToCron(schedule) {
  switch (schedule.kind) {
    case "interval":
      return `*/${clamp(schedule.everyMinutes, 1, 59)} * * * *`;
    case "hourly":
      return `${clamp(schedule.minute, 0, 59)} * * * *`;
    case "daily":
      return `${clamp(schedule.minute, 0, 59)} ${clamp(schedule.hour, 0, 23)} * * *`;
    case "custom":
      return schedule.cron;
  }
}
function cronToSchedule(cron) {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { kind: "custom", cron };
  const [min, hour, dom, mon, dow] = parts;
  if (dom !== "*" || mon !== "*" || dow !== "*") return { kind: "custom", cron };
  const interval = /^\*\/(\d{1,2})$/.exec(min);
  if (interval && hour === "*") {
    const every = Number(interval[1]);
    if (every >= 1 && every <= 59) return { kind: "interval", everyMinutes: every };
  }
  if (/^\d{1,2}$/.test(min)) {
    const minute = Number(min);
    if (minute > 59) return { kind: "custom", cron };
    if (hour === "*") return { kind: "hourly", minute };
    if (/^\d{1,2}$/.test(hour)) {
      const h = Number(hour);
      if (h <= 23) return { kind: "daily", hour: h, minute };
    }
  }
  return { kind: "custom", cron };
}
function describeSchedule(schedule) {
  const pad = (n) => String(n).padStart(2, "0");
  switch (schedule.kind) {
    case "interval":
      return `\u043A\u0430\u0436\u0434\u044B\u0435 ${schedule.everyMinutes} \u043C\u0438\u043D`;
    case "hourly":
      return `\u043A\u0430\u0436\u0434\u044B\u0439 \u0447\u0430\u0441 \u0432 :${pad(schedule.minute)}`;
    case "daily":
      return `\u043A\u0430\u0436\u0434\u044B\u0439 \u0434\u0435\u043D\u044C \u0432 ${pad(schedule.hour)}:${pad(schedule.minute)}`;
    case "custom":
      return `\u043F\u043E \u0440\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044E ${schedule.cron}`;
  }
}

// src/networks.ts
var CRYPTO_NETWORKS = [
  {
    id: "TRC20",
    label: "TRC-20 (Tron)",
    explorerTx: "https://tronscan.org/#/transaction/{hash}",
    tokens: ["USDT", "TRX", "USDC", "USDD", "TUSD", "JST", "SUN", "BTT", "WIN", "NFT"]
  },
  {
    id: "ERC20",
    label: "ERC-20 (Ethereum)",
    explorerTx: "https://etherscan.io/tx/{hash}",
    tokens: ["USDT", "USDC", "ETH", "DAI", "WBTC", "LINK", "UNI", "SHIB", "PEPE", "AAVE"]
  },
  {
    id: "BEP20",
    label: "BEP-20 (BNB Chain)",
    explorerTx: "https://bscscan.com/tx/{hash}",
    tokens: ["USDT", "USDC", "BNB", "BUSD", "CAKE", "ETH", "BTCB", "DOGE", "XRP", "ADA"]
  },
  {
    id: "TON",
    label: "TON",
    explorerTx: "https://tonviewer.com/transaction/{hash}",
    tokens: ["USDT", "TON", "NOT", "DOGS", "STON", "HMSTR", "CATI", "JUSDT", "SCALE", "GRAM"]
  },
  {
    id: "SOL",
    label: "Solana",
    explorerTx: "https://solscan.io/tx/{hash}",
    tokens: ["USDT", "USDC", "SOL", "JUP", "BONK", "RAY", "JTO", "PYTH", "WIF", "MSOL"]
  },
  {
    id: "POLYGON",
    label: "Polygon",
    explorerTx: "https://polygonscan.com/tx/{hash}",
    tokens: ["USDT", "USDC", "POL", "DAI", "WETH", "WBTC", "LINK", "AAVE", "QUICK", "SAND"]
  },
  {
    id: "ARBITRUM",
    label: "Arbitrum One",
    explorerTx: "https://arbiscan.io/tx/{hash}",
    tokens: ["USDT", "USDC", "ETH", "ARB", "DAI", "WBTC", "LINK", "GMX", "PENDLE", "MAGIC"]
  },
  {
    id: "BASE",
    label: "Base",
    explorerTx: "https://basescan.org/tx/{hash}",
    tokens: ["USDC", "USDT", "ETH", "DAI", "CBBTC", "AERO", "DEGEN", "BRETT", "TOSHI", "WELL"]
  },
  {
    id: "OPTIMISM",
    label: "Optimism",
    explorerTx: "https://optimistic.etherscan.io/tx/{hash}",
    tokens: ["USDT", "USDC", "ETH", "OP", "DAI", "WBTC", "LINK", "SNX", "VELO", "WLD"]
  },
  {
    id: "AVAX",
    label: "Avalanche C-Chain",
    explorerTx: "https://snowtrace.io/tx/{hash}",
    tokens: ["USDT", "USDC", "AVAX", "DAI", "WAVAX", "WBTC", "LINK", "JOE", "SAVAX", "QI"]
  },
  {
    id: "BTC",
    label: "Bitcoin",
    explorerTx: "https://blockchair.com/bitcoin/transaction/{hash}",
    tokens: ["BTC"]
  },
  {
    id: "LTC",
    label: "Litecoin",
    explorerTx: "https://blockchair.com/litecoin/transaction/{hash}",
    tokens: ["LTC"]
  },
  {
    id: "XRP",
    label: "XRP Ledger",
    explorerTx: "https://xrpscan.com/tx/{hash}",
    tokens: ["XRP", "RLUSD"]
  },
  {
    id: "DOGE",
    label: "Dogecoin",
    explorerTx: "https://blockchair.com/dogecoin/transaction/{hash}",
    tokens: ["DOGE"]
  },
  {
    id: "ZKSYNC",
    label: "zkSync Era",
    explorerTx: "https://explorer.zksync.io/tx/{hash}",
    tokens: ["USDC", "USDT", "ETH", "ZK", "DAI", "WBTC", "LINK", "WETH", "BUSD", "MUTE"]
  },
  {
    id: "LINEA",
    label: "Linea",
    explorerTx: "https://lineascan.build/tx/{hash}",
    tokens: ["USDC", "USDT", "ETH", "DAI", "WBTC", "WETH", "LINK", "WSTETH", "EZETH", "FOXY"]
  },
  {
    id: "CRONOS",
    label: "Cronos",
    explorerTx: "https://cronoscan.com/tx/{hash}",
    tokens: ["USDT", "USDC", "CRO", "WCRO", "DAI", "WETH", "WBTC", "VVS", "TONIC", "MMF"]
  },
  {
    id: "APTOS",
    label: "Aptos",
    explorerTx: "https://explorer.aptoslabs.com/txn/{hash}",
    tokens: ["USDT", "USDC", "APT", "THL", "AMI", "CELL", "STAPT", "ABEL", "MOD", "ZUSDC"]
  },
  {
    id: "SUI",
    label: "Sui",
    explorerTx: "https://suiscan.xyz/mainnet/tx/{hash}",
    tokens: ["USDC", "USDT", "SUI", "CETUS", "DEEP", "AFSUI", "NAVX", "BUCK", "WAL", "TURBOS"]
  },
  {
    id: "NEAR",
    label: "NEAR",
    explorerTx: "https://nearblocks.io/txns/{hash}",
    tokens: ["USDT", "USDC", "NEAR", "REF", "AURORA", "WNEAR", "STNEAR", "META", "OCT", "PEM"]
  }
];
var DEFAULT_NETWORK = "TRC20";
var DEFAULT_TOKEN = "USDT";
var PAYMENT_METHODS = ["crypto", "fiat"];
var findNetwork = (id) => CRYPTO_NETWORKS.find((n) => n.id === id);
var tokensForNetwork = (id) => findNetwork(id)?.tokens ?? [DEFAULT_TOKEN];
var looksLikeUrl = (value) => /^https?:\/\//i.test(value);
function extractHash(raw) {
  const value = (raw ?? "").trim();
  if (!value) return "";
  if (!looksLikeUrl(value)) return value;
  const segments = value.split(/[/#?&=]/).filter((seg) => /^[A-Za-z0-9]{16,}$/.test(seg)).sort((a, b) => b.length - a.length);
  return segments[0] ?? value;
}
function explorerUrl(networkId, hash) {
  const value = (hash ?? "").trim();
  if (!value) return null;
  if (looksLikeUrl(value)) return value;
  const network = findNetwork(networkId);
  if (!network) return null;
  return network.explorerTx.replace("{hash}", encodeURIComponent(value));
}
function explorerAddressUrl(networkId, address) {
  const value = (address ?? "").trim();
  const network = findNetwork(networkId);
  if (!value || !network) return null;
  const template = network.explorerTx.replace(/(transaction|tx)\/\{hash\}/, "address/{hash}");
  return template === network.explorerTx ? null : template.replace("{hash}", encodeURIComponent(value));
}
function shortHash(hash, lead = 8, tail = 6) {
  const h = extractHash(hash);
  return h.length <= lead + tail + 1 ? h : `${h.slice(0, lead)}\u2026${h.slice(-tail)}`;
}

// src/permissions.ts
var PERMISSIONS = [
  { key: "stats.view", group: "\u0421\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0430", label: "\u0421\u043C\u043E\u0442\u0440\u0435\u0442\u044C \u0441\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0443" },
  { key: "clients.view", group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B", label: "\u0421\u043C\u043E\u0442\u0440\u0435\u0442\u044C \u043A\u043B\u0438\u0435\u043D\u0442\u043E\u0432" },
  { key: "clients.manage", group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B", label: "\u0421\u043E\u0437\u0434\u0430\u0432\u0430\u0442\u044C \u0438 \u0440\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043A\u043B\u0438\u0435\u043D\u0442\u043E\u0432" },
  { key: "clients.topup", group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B", label: "\u041F\u0440\u043E\u0432\u043E\u0434\u0438\u0442\u044C \u0442\u0440\u0430\u043D\u0437\u0430\u043A\u0446\u0438\u0438" },
  { key: "clients.tiers", group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B", label: "\u041C\u0435\u043D\u044F\u0442\u044C \u043A\u043E\u043C\u0438\u0441\u0441\u0438\u044E" },
  { key: "clients.notify", group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B", label: "\u041E\u0442\u043F\u0440\u0430\u0432\u043B\u044F\u0442\u044C \u0441\u0432\u043E\u0434\u043A\u0438 \u0432 \u0447\u0430\u0442" },
  { key: "accounts.view", group: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B", label: "\u0421\u043C\u043E\u0442\u0440\u0435\u0442\u044C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u044B" },
  { key: "accounts.manage", group: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B", label: "\u041F\u0440\u0438\u0432\u044F\u0437\u044B\u0432\u0430\u0442\u044C \u0438 \u043E\u0442\u0441\u043B\u0435\u0436\u0438\u0432\u0430\u0442\u044C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u044B" },
  { key: "accounts.credentials", group: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B", label: "\u0423\u043F\u0440\u0430\u0432\u043B\u044F\u0442\u044C API-\u043A\u043B\u044E\u0447\u0430\u043C\u0438 Moloco" },
  { key: "accounts.sync", group: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B", label: "\u0417\u0430\u043F\u0443\u0441\u043A\u0430\u0442\u044C \u0441\u0438\u043D\u0445\u0440\u043E\u043D\u0438\u0437\u0430\u0446\u0438\u044E \u0432\u0440\u0443\u0447\u043D\u0443\u044E" },
  { key: "settings.view", group: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438", label: "\u0421\u043C\u043E\u0442\u0440\u0435\u0442\u044C \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438" },
  { key: "settings.manage", group: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438", label: "\u041C\u0435\u043D\u044F\u0442\u044C \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438" },
  { key: "broadcast.send", group: "\u0420\u0430\u0441\u0441\u044B\u043B\u043A\u0438", label: "\u041E\u0442\u043F\u0440\u0430\u0432\u043B\u044F\u0442\u044C \u0440\u0430\u0441\u0441\u044B\u043B\u043A\u0438 \u043A\u043B\u0438\u0435\u043D\u0442\u0430\u043C" },
  { key: "apprequests.view", group: "\u0417\u0430\u044F\u0432\u043A\u0438", label: "\u0421\u043C\u043E\u0442\u0440\u0435\u0442\u044C \u0437\u0430\u044F\u0432\u043A\u0438 \u043D\u0430 \u0434\u043E\u0431\u0430\u0432\u043B\u0435\u043D\u0438\u0435 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u0439" },
  { key: "apprequests.manage", group: "\u0417\u0430\u044F\u0432\u043A\u0438", label: "\u041C\u0435\u043D\u044F\u0442\u044C \u0441\u0442\u0430\u0442\u0443\u0441 \u0438 \u0443\u0434\u0430\u043B\u044F\u0442\u044C \u0437\u0430\u044F\u0432\u043A\u0438" },
  { key: "pnl.view", group: "\u0424\u0438\u043D\u0430\u043D\u0441\u044B", label: "\u0421\u043C\u043E\u0442\u0440\u0435\u0442\u044C P&L" },
  { key: "pnl.manage", group: "\u0424\u0438\u043D\u0430\u043D\u0441\u044B", label: "\u0412\u0435\u0441\u0442\u0438 \u0440\u0430\u0441\u0445\u043E\u0434\u044B" },
  { key: "wallets.view", group: "\u041A\u043E\u0448\u0435\u043B\u044C\u043A\u0438", label: "\u0421\u043C\u043E\u0442\u0440\u0435\u0442\u044C \u043A\u043E\u0448\u0435\u043B\u044C\u043A\u0438 \u0438 \u043E\u0431\u043E\u0437\u0440\u0435\u0432\u0430\u0442\u0435\u043B\u044C \u0431\u043B\u043E\u043A\u0447\u0435\u0439\u043D\u0430" },
  { key: "wallets.manage", group: "\u041A\u043E\u0448\u0435\u043B\u044C\u043A\u0438", label: "\u0414\u043E\u0431\u0430\u0432\u043B\u044F\u0442\u044C \u0438 \u043C\u0435\u043D\u044F\u0442\u044C \u043A\u043E\u0448\u0435\u043B\u044C\u043A\u0438 \u0434\u043B\u044F \u043F\u043E\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u044F" },
  { key: "bots.view", group: "\u0422\u0435\u043B\u0435\u0433\u0440\u0430\u043C-\u0431\u043E\u0442\u044B", label: "\u0421\u043C\u043E\u0442\u0440\u0435\u0442\u044C \u0431\u043E\u0442\u043E\u0432 \u0438 \u0438\u0445 \u043A\u043E\u043C\u0430\u043D\u0434\u044B" },
  { key: "bots.manage", group: "\u0422\u0435\u043B\u0435\u0433\u0440\u0430\u043C-\u0431\u043E\u0442\u044B", label: "\u041C\u0435\u043D\u044F\u0442\u044C \u0442\u043E\u043A\u0435\u043D\u044B \u0438 \u043C\u0435\u043D\u044E \u0431\u043E\u0442\u043E\u0432" },
  { key: "mcp.use", group: "MCP", label: "\u041F\u043E\u0434\u043A\u043B\u044E\u0447\u0430\u0442\u044C\u0441\u044F \u0447\u0435\u0440\u0435\u0437 MCP (\u0418\u0418-\u043A\u043B\u0438\u0435\u043D\u0442\u044B)" },
  { key: "users.manage", group: "\u0414\u043E\u0441\u0442\u0443\u043F\u044B", label: "\u0423\u043F\u0440\u0430\u0432\u043B\u044F\u0442\u044C \u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u044F\u043C\u0438 \u0438 \u0440\u043E\u043B\u044F\u043C\u0438" }
];
var ALL_PERMISSIONS = PERMISSIONS.map((p) => p.key);
var PERMISSION_GROUPS = [...new Set(PERMISSIONS.map((p) => p.group))];
var SUPER_ADMIN_SLUG = "super-admin";
function roleHasPermission(role, permission) {
  if (!role) return false;
  if (role.slug === SUPER_ADMIN_SLUG) return true;
  return role.permissions.includes(permission);
}
var isValidPermission = (value) => ALL_PERMISSIONS.includes(value);

// src/broadcast.ts
import * as z2 from "zod";
var BROADCAST_KINDS = [
  { kind: "text", label: "\u0422\u0435\u043A\u0441\u0442", method: "sendMessage", captionable: false, accept: "" },
  { kind: "photo", label: "\u0424\u043E\u0442\u043E", method: "sendPhoto", captionable: true, accept: "image/*" },
  { kind: "video", label: "\u0412\u0438\u0434\u0435\u043E", method: "sendVideo", captionable: true, accept: "video/*" },
  {
    kind: "animation",
    label: "GIF / \u0430\u043D\u0438\u043C\u0430\u0446\u0438\u044F",
    method: "sendAnimation",
    captionable: true,
    accept: "image/gif,video/mp4"
  },
  { kind: "document", label: "\u0414\u043E\u043A\u0443\u043C\u0435\u043D\u0442", method: "sendDocument", captionable: true, accept: "" },
  { kind: "audio", label: "\u0410\u0443\u0434\u0438\u043E", method: "sendAudio", captionable: true, accept: "audio/*" },
  { kind: "voice", label: "\u0413\u043E\u043B\u043E\u0441\u043E\u0432\u043E\u0435", method: "sendVoice", captionable: true, accept: "audio/ogg,audio/*" },
  { kind: "video_note", label: "\u041A\u0440\u0443\u0436\u043E\u043A", method: "sendVideoNote", captionable: false, accept: "video/mp4" },
  { kind: "sticker", label: "\u0421\u0442\u0438\u043A\u0435\u0440", method: "sendSticker", captionable: false, accept: "image/webp" }
];
var findKind = (kind) => BROADCAST_KINDS.find((k) => k.kind === kind);
var MEDIA_FIELD = {
  text: "",
  photo: "photo",
  video: "video",
  animation: "animation",
  document: "document",
  audio: "audio",
  voice: "voice",
  video_note: "video_note",
  sticker: "sticker"
};
var kindNeedsMedia = (kind) => kind !== "text";
var BUTTON_TYPES = [
  { type: "url", label: "\u0421\u0441\u044B\u043B\u043A\u0430", needsValue: true, hint: "https://example.com" },
  { type: "copy_text", label: "\u041A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u0442\u0435\u043A\u0441\u0442", needsValue: true, hint: "\u0442\u0435\u043A\u0441\u0442 \u0434\u043B\u044F \u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u044F" },
  { type: "switch_inline", label: "Inline \u0432 \u0434\u0440\u0443\u0433\u043E\u043C \u0447\u0430\u0442\u0435", needsValue: true, hint: "\u0437\u0430\u043F\u0440\u043E\u0441" },
  { type: "switch_inline_current", label: "Inline \u0432 \u044D\u0442\u043E\u043C \u0447\u0430\u0442\u0435", needsValue: true, hint: "\u0437\u0430\u043F\u0440\u043E\u0441" },
  { type: "callback", label: "Callback", needsValue: true, hint: "\u0434\u0430\u043D\u043D\u044B\u0435 (\u043D\u0443\u0436\u0435\u043D \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u0447\u0438\u043A)" }
];
var MAX_BUTTONS_PER_ROW = 8;
var MAX_BUTTON_ROWS = 10;
var BUTTON_STYLES = [
  { style: "", label: "\u041E\u0431\u044B\u0447\u043D\u0430\u044F", hint: "\u043D\u0435\u0439\u0442\u0440\u0430\u043B\u044C\u043D\u043E\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0435" },
  { style: "primary", label: "\u041E\u0441\u043D\u043E\u0432\u043D\u0430\u044F", hint: "\u0441\u0438\u043D\u044F\u044F \u2014 \u0433\u043B\u0430\u0432\u043D\u043E\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0435" },
  { style: "success", label: "\u0423\u0441\u043F\u0435\u0445", hint: "\u0437\u0435\u043B\u0451\u043D\u0430\u044F \u2014 \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0435" },
  { style: "danger", label: "\u041E\u043F\u0430\u0441\u043D\u0430\u044F", hint: "\u043A\u0440\u0430\u0441\u043D\u0430\u044F \u2014 \u043D\u0435\u043E\u0431\u0440\u0430\u0442\u0438\u043C\u043E\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0435" }
];
var buttonSchema = z2.object({
  text: z2.string().min(1).max(64),
  type: z2.enum(["url", "copy_text", "switch_inline", "switch_inline_current", "callback"]),
  value: z2.string().min(1).max(256),
  style: z2.enum(["", "primary", "success", "danger"]).default("")
});
var buttonRowsSchema = z2.array(z2.array(buttonSchema).max(MAX_BUTTONS_PER_ROW)).max(MAX_BUTTON_ROWS);
function toInlineKeyboard(rows) {
  const action = (b) => {
    switch (b.type) {
      case "url":
        return { url: b.value };
      case "copy_text":
        return { copy_text: { text: b.value } };
      case "switch_inline":
        return { switch_inline_query: b.value };
      case "switch_inline_current":
        return { switch_inline_query_current_chat: b.value };
      case "callback":
        return { callback_data: b.value.slice(0, 64) };
    }
  };
  const keyboard = rows.map(
    (row) => row.map((b) => ({
      text: b.text,
      ...action(b),
      // Omitted entirely when unset: sending style:"" is not the same as
      // sending nothing, and only the latter means "client default".
      ...b.style ? { style: b.style } : {}
    }))
  ).filter((row) => row.length > 0);
  return keyboard.length > 0 ? { inline_keyboard: keyboard } : null;
}
var createBroadcastBody = z2.object({
  kind: z2.enum(["text", "photo", "video", "animation", "document", "audio", "voice", "video_note", "sticker"]),
  audience: z2.enum(["all", "selected", "one"]),
  targetClientIds: z2.array(z2.number().int().positive()).default([]),
  text: z2.string().max(4096).optional(),
  mediaPath: z2.string().max(400).optional(),
  mediaName: z2.string().max(200).optional(),
  mediaMime: z2.string().max(120).optional(),
  buttons: buttonRowsSchema.default([]),
  disablePreview: z2.boolean().default(true),
  silent: z2.boolean().default(false),
  /** ISO timestamp. Omitted or in the past means send now. */
  scheduledAt: z2.string().datetime({ offset: true }).nullable().optional()
});
var broadcastSchema = z2.object({
  id: z2.number(),
  kind: z2.string(),
  audience: z2.string(),
  targetClientIds: z2.array(z2.number()),
  text: z2.string().nullable(),
  mediaName: z2.string().nullable(),
  mediaMime: z2.string().nullable(),
  buttons: z2.array(z2.array(buttonSchema)),
  status: z2.string(),
  scheduledAt: z2.string().nullable(),
  totalCount: z2.number(),
  sentCount: z2.number(),
  failedCount: z2.number(),
  error: z2.string().nullable(),
  createdAt: z2.string(),
  finishedAt: z2.string().nullable()
});
var broadcastDeliverySchema = z2.object({
  id: z2.number(),
  clientTitle: z2.string().nullable(),
  chatId: z2.string(),
  status: z2.string(),
  messageId: z2.number().nullable(),
  error: z2.string().nullable(),
  sentAt: z2.string().nullable(),
  deletedAt: z2.string().nullable()
});
var DELETE_WINDOW_HOURS = 48;
var BROADCAST_RATE_PER_SECOND = 15;

// src/budget.ts
var BUDGET_PERIODS = ["daily", "weekly"];
var BUDGET_PERIOD_LABELS = {
  daily: "\u0432 \u0434\u0435\u043D\u044C",
  weekly: "\u0432 \u043D\u0435\u0434\u0435\u043B\u044E"
};
function dailyBudgetMicros(budgetMicros, period) {
  if (budgetMicros === null || budgetMicros === void 0 || budgetMicros === "") return null;
  const raw = typeof budgetMicros === "bigint" ? budgetMicros : BigInt(String(budgetMicros).split(".")[0] ?? "0");
  if (period === "weekly") return raw / 7n;
  return raw;
}
function isCampaignLive(state, enablingState) {
  return state === "ACTIVE" && enablingState === "ENABLED";
}
var budgetToAmount = (micros) => micros === null ? null : fromMicros(micros, 2);

// src/mcp.ts
var MCP_TOOLS = [
  { name: "whoami", title: "\u041A\u0442\u043E \u044F \u0438 \u043A\u0430\u043A\u0438\u0435 \u0443 \u043C\u0435\u043D\u044F \u043F\u0440\u0430\u0432\u0430", group: "\u0414\u043E\u0441\u0442\u0443\u043F", kind: "read", permission: null },
  {
    name: "get_stats",
    title: "\u0421\u0432\u043E\u0434\u043D\u0430\u044F \u0441\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0430 \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434",
    group: "\u0421\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0430",
    kind: "read",
    permission: "stats.view"
  },
  { name: "get_spend_by_day", title: "\u0420\u0430\u0441\u0445\u043E\u0434 \u043F\u043E \u0434\u043D\u044F\u043C", group: "\u0421\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0430", kind: "read", permission: "stats.view" },
  {
    name: "get_budget_by_client",
    title: "\u041A\u0442\u043E \u0441\u043A\u043E\u043B\u044C\u043A\u043E \u0437\u0430\u043F\u0443\u0441\u0442\u0438\u043B",
    group: "\u0421\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0430",
    kind: "read",
    permission: "accounts.view"
  },
  { name: "list_clients", title: "\u0421\u043F\u0438\u0441\u043E\u043A \u043A\u043B\u0438\u0435\u043D\u0442\u043E\u0432", group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B", kind: "read", permission: "clients.view" },
  {
    name: "get_client",
    title: "\u041A\u043B\u0438\u0435\u043D\u0442: \u0431\u0430\u043B\u0430\u043D\u0441\u044B \u0438 \u0442\u0440\u0430\u043D\u0437\u0430\u043A\u0446\u0438\u0438",
    group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B",
    kind: "read",
    permission: "clients.view"
  },
  {
    name: "list_transactions",
    title: "\u0422\u0440\u0430\u043D\u0437\u0430\u043A\u0446\u0438\u0438 \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434",
    group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B",
    kind: "read",
    permission: "clients.view"
  },
  { name: "add_deposit", title: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u043F\u043E\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u0435", group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B", kind: "write", permission: "clients.topup" },
  { name: "add_refund", title: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0432\u043E\u0437\u0432\u0440\u0430\u0442", group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B", kind: "write", permission: "clients.topup" },
  {
    name: "edit_transaction",
    title: "\u0418\u0441\u043F\u0440\u0430\u0432\u0438\u0442\u044C \u043F\u0440\u043E\u0432\u0435\u0434\u0451\u043D\u043D\u0443\u044E \u0442\u0440\u0430\u043D\u0437\u0430\u043A\u0446\u0438\u044E",
    group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B",
    kind: "write",
    permission: "clients.topup"
  },
  {
    name: "fund_cabinet",
    title: "\u041F\u043E\u043F\u043E\u043B\u043D\u0438\u0442\u044C \u043A\u0430\u0431\u0438\u043D\u0435\u0442 \u0441\u043E \u0441\u0447\u0451\u0442\u0430",
    group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B",
    kind: "write",
    permission: "clients.topup"
  },
  {
    name: "return_cabinet_balance",
    title: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C \u043E\u0441\u0442\u0430\u0442\u043E\u043A \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430 \u043D\u0430 \u0441\u0447\u0451\u0442",
    group: "\u041A\u043B\u0438\u0435\u043D\u0442\u044B",
    kind: "write",
    permission: "clients.topup"
  },
  { name: "list_accounts", title: "\u0421\u043F\u0438\u0441\u043E\u043A \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u043E\u0432", group: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B", kind: "read", permission: "accounts.view" },
  {
    name: "assign_account",
    title: "\u041F\u0440\u0438\u0432\u044F\u0437\u0430\u0442\u044C \u043A\u0430\u0431\u0438\u043D\u0435\u0442 \u043A \u043A\u043B\u0438\u0435\u043D\u0442\u0443",
    group: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442\u044B",
    kind: "write",
    permission: "accounts.manage"
  },
  { name: "list_campaigns", title: "\u0421\u043F\u0438\u0441\u043E\u043A \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0439", group: "\u041A\u0430\u043C\u043F\u0430\u043D\u0438\u0438", kind: "read", permission: "accounts.view" },
  {
    name: "get_campaign_spend",
    title: "\u0420\u0430\u0441\u0445\u043E\u0434 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0439 \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434",
    group: "\u041A\u0430\u043C\u043F\u0430\u043D\u0438\u0438",
    kind: "read",
    permission: "accounts.view"
  },
  {
    name: "list_app_requests",
    title: "\u0417\u0430\u044F\u0432\u043A\u0438 \u043D\u0430 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u044F",
    group: "\u0417\u0430\u044F\u0432\u043A\u0438",
    kind: "read",
    permission: "apprequests.view"
  },
  {
    name: "set_app_request_status",
    title: "\u0421\u043C\u0435\u043D\u0438\u0442\u044C \u0441\u0442\u0430\u0442\u0443\u0441 \u0437\u0430\u044F\u0432\u043A\u0438",
    group: "\u0417\u0430\u044F\u0432\u043A\u0438",
    kind: "write",
    permission: "apprequests.manage"
  },
  {
    name: "delete_app_request",
    title: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0437\u0430\u044F\u0432\u043A\u0443",
    group: "\u0417\u0430\u044F\u0432\u043A\u0438",
    kind: "write",
    permission: "apprequests.manage"
  },
  {
    name: "get_spend_report",
    title: "\u041E\u0442\u0447\u0451\u0442 \u043F\u043E \u0441\u043F\u0435\u043D\u0434\u0443: \u043F\u043E \u0434\u043D\u044F\u043C/\u043D\u0435\u0434\u0435\u043B\u044F\u043C/\u043C\u0435\u0441\u044F\u0446\u0430\u043C",
    group: "\u041E\u0442\u0447\u0451\u0442\u044B",
    kind: "read",
    permission: "stats.view"
  },
  {
    name: "get_transactions_report",
    title: "\u0421\u0432\u043E\u0434\u043A\u0430 \u043F\u043E \u0442\u0440\u0430\u043D\u0437\u0430\u043A\u0446\u0438\u044F\u043C \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434",
    group: "\u041E\u0442\u0447\u0451\u0442\u044B",
    kind: "read",
    permission: "clients.view"
  },
  {
    name: "get_client_forecast",
    title: "\u041F\u0440\u043E\u0433\u043D\u043E\u0437 \u0431\u0430\u043B\u0430\u043D\u0441\u0430 \u043A\u043B\u0438\u0435\u043D\u0442\u0430",
    group: "\u041E\u0442\u0447\u0451\u0442\u044B",
    kind: "read",
    permission: "clients.view"
  },
  { name: "get_pnl", title: "P&L \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434", group: "\u041E\u0442\u0447\u0451\u0442\u044B", kind: "read", permission: "pnl.view" },
  { name: "list_expenses", title: "\u0420\u0430\u0441\u0445\u043E\u0434\u044B \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434", group: "\u041E\u0442\u0447\u0451\u0442\u044B", kind: "read", permission: "pnl.view" },
  {
    name: "export_client_report",
    title: "\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0430 xlsx \u043F\u043E \u043A\u043B\u0438\u0435\u043D\u0442\u0443",
    group: "\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0438",
    kind: "read",
    permission: "clients.view"
  },
  {
    name: "export_campaign_report",
    title: "\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0430 xlsx \u043F\u043E \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u044F\u043C",
    group: "\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0438",
    kind: "read",
    permission: "accounts.view"
  },
  {
    name: "export_stats_report",
    title: "\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0430 xlsx: \u043A\u043B\u0438\u0435\u043D\u0442\u044B, \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u044B, \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438",
    group: "\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0438",
    kind: "read",
    permission: "clients.view"
  },
  { name: "export_pnl", title: "\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0430 P&L (CSV)", group: "\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0438", kind: "read", permission: "pnl.view" },
  {
    name: "test_confirmation",
    title: "\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C \u043F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0435 (\u043D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043C\u0435\u043D\u044F\u0435\u0442)",
    group: "\u0414\u043E\u0441\u0442\u0443\u043F",
    kind: "read",
    permission: null
  },
  { name: "confirm_action", title: "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0435", group: "\u0414\u043E\u0441\u0442\u0443\u043F", kind: "write", permission: null }
];
var findMcpTool = (name) => MCP_TOOLS.find((t) => t.name === name);

// src/bots.ts
var LIVE_BOT_PROVIDERS = [
  "moloco",
  "unity",
  "tiktok",
  "outbrain",
  "taboola"
];
var isLiveBotProvider = (value) => LIVE_BOT_PROVIDERS.includes(value);
var botWebhookPath = (provider) => provider === "moloco" ? "/telegram/webhook" : `/telegram/webhook/${provider}`;
var BOT_COMMANDS = [
  {
    command: "spend",
    aliases: ["stats", "today"],
    description: "\u0421\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0430: \u0441\u043F\u0435\u043D\u0434 \u0438 \u043E\u0441\u0442\u0430\u0442\u043E\u043A",
    help: "\u0442\u0435\u043A\u0443\u0449\u0430\u044F \u0441\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0430: \u0441\u043F\u0435\u043D\u0434 \u0441\u0435\u0433\u043E\u0434\u043D\u044F, \u0441\u043F\u0435\u043D\u0434 \u0432\u0441\u0435\u0433\u043E, \u043E\u0441\u0442\u0430\u0442\u043E\u043A \u043D\u0430 \u0431\u0430\u043B\u0430\u043D\u0441\u0435",
    access: "all",
    menu: true
  },
  {
    command: "deposit",
    description: "\u0420\u0435\u043A\u0432\u0438\u0437\u0438\u0442\u044B \u0434\u043B\u044F \u043F\u043E\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u044F",
    help: "\u0440\u0435\u043A\u0432\u0438\u0437\u0438\u0442\u044B \u0434\u043B\u044F \u043F\u043E\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u044F: \u0441\u0435\u0442\u044C, \u0442\u043E\u043A\u0435\u043D, \u0430\u0434\u0440\u0435\u0441 \u0438 QR",
    access: "all",
    menu: true
  },
  {
    command: "requestapp",
    description: "\u0417\u0430\u044F\u0432\u043A\u0430 \u043D\u0430 \u0434\u043E\u0431\u0430\u0432\u043B\u0435\u043D\u0438\u0435 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u044F",
    help: "\u0437\u0430\u044F\u0432\u043A\u0430 \u043D\u0430 \u0434\u043E\u0431\u0430\u0432\u043B\u0435\u043D\u0438\u0435 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u044F \u0432 \u043A\u0430\u0431\u0438\u043D\u0435\u0442",
    access: "group",
    menu: true,
    platforms: ["moloco"]
  },
  {
    command: "rulesmoloco",
    description: "\u041F\u0440\u0430\u0432\u0438\u043B\u0430 \u0437\u0430\u043F\u0443\u0441\u043A\u0430 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0439",
    help: "\u043F\u0440\u0430\u0432\u0438\u043B\u0430 \u0437\u0430\u043F\u0443\u0441\u043A\u0430 \u0440\u0435\u043A\u043B\u0430\u043C\u043D\u044B\u0445 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0439",
    access: "all",
    menu: true,
    platforms: ["moloco"]
  },
  {
    command: "export",
    description: "\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0430 xlsx \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434",
    help: "\u0432\u044B\u0433\u0440\u0443\u0437\u043A\u0430 xlsx \u0437\u0430 \u043F\u0435\u0440\u0438\u043E\u0434: \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u044B \u0441\u043E \u0441\u043F\u0435\u043D\u0434\u043E\u043C \u043F\u043E \u0434\u043D\u044F\u043C, \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438, \u043F\u043E\u043F\u043E\u043B\u043D\u0435\u043D\u0438\u044F",
    access: "group",
    menu: true
  },
  { command: "id", description: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C chat_id", help: "\u0443\u0437\u043D\u0430\u0442\u044C chat_id \u044D\u0442\u043E\u0433\u043E \u0447\u0430\u0442\u0430", access: "all", menu: true },
  { command: "help", description: "\u0421\u043F\u0438\u0441\u043E\u043A \u043A\u043E\u043C\u0430\u043D\u0434", help: "\u044D\u0442\u043E \u0441\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0435", access: "all", menu: true },
  {
    command: "test",
    description: "\u0424\u043E\u0440\u043C\u0430 \u0437\u0430\u044F\u0432\u043A\u0438 \u043E\u0442 \u0438\u043C\u0435\u043D\u0438 \u0447\u0430\u0442\u0430",
    help: "\u0444\u043E\u0440\u043C\u0430 \u0437\u0430\u044F\u0432\u043A\u0438 \u043A\u0430\u043A \u0432 \u0447\u0430\u0442\u0435 \u043A\u043B\u0438\u0435\u043D\u0442\u0430: /test \u0438\u043B\u0438 /test <chat_id>",
    access: "admin-sender",
    menu: false,
    platforms: ["moloco"]
  },
  { command: "total", description: "\u041E\u0431\u0449\u0438\u0439 \u0441\u043F\u0435\u043D\u0434", help: "\u043E\u0431\u0449\u0438\u0439 \u0441\u043F\u0435\u043D\u0434 \u043F\u043E \u0432\u0441\u0435\u043C \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430\u043C", access: "admin", menu: false },
  {
    command: "clients",
    description: "\u0421\u043F\u0435\u043D\u0434 \u043F\u043E \u043A\u043B\u0438\u0435\u043D\u0442\u0430\u043C",
    help: "\u0441\u043F\u0435\u043D\u0434 \u0438 \u0431\u0430\u043B\u0430\u043D\u0441 \u043F\u043E \u043A\u0430\u0436\u0434\u043E\u043C\u0443 \u043A\u043B\u0438\u0435\u043D\u0442\u0443",
    access: "admin",
    menu: false
  },
  { command: "admin", description: "\u0410\u0434\u043C\u0438\u043D-\u043A\u043E\u043C\u0430\u043D\u0434\u044B", help: "\u044D\u0442\u043E\u0442 \u0441\u043F\u0438\u0441\u043E\u043A", access: "admin", menu: false }
];
var botCommandsFor = (provider) => isLiveBotProvider(provider) ? BOT_COMMANDS.filter((c) => !c.platforms || c.platforms.includes(provider)) : [];
var BOT_COMMAND_ACCESS_LABELS = {
  all: "\u0412\u0441\u0435, \u0432 \u043B\u044E\u0431\u043E\u043C \u0447\u0430\u0442\u0435",
  group: "\u0412\u0441\u0435, \u0432 \u0447\u0430\u0442\u0435 \u043A\u043B\u0438\u0435\u043D\u0442\u0430",
  admin: "\u0410\u0434\u043C\u0438\u043D-\u0447\u0430\u0442 \u0438 \u0430\u0434\u043C\u0438\u043D\u044B \u0432 \u043B\u0438\u0447\u043A\u0435",
  "admin-sender": "\u0410\u0434\u043C\u0438\u043D\u044B, \u0432 \u043B\u044E\u0431\u043E\u043C \u0447\u0430\u0442\u0435"
};

// src/pnl.ts
var EXPENSE_CATEGORIES = [
  "\u041A\u043E\u043C\u0438\u0441\u0441\u0438\u0438 \u0438 \u0441\u0431\u043E\u0440\u044B \u043F\u043B\u0430\u0442\u0451\u0436\u043D\u044B\u0445 \u0441\u0435\u0440\u0432\u0438\u0441\u043E\u0432",
  "\u0418\u043D\u0444\u0440\u0430\u0441\u0442\u0440\u0443\u043A\u0442\u0443\u0440\u0430 \u0438 \u0441\u0435\u0440\u0432\u0438\u0441\u044B",
  "\u0417\u0430\u0440\u043F\u043B\u0430\u0442\u044B \u0438 \u043F\u043E\u0434\u0440\u044F\u0434\u0447\u0438\u043A\u0438",
  "\u041C\u0430\u0440\u043A\u0435\u0442\u0438\u043D\u0433",
  "\u041D\u0430\u043B\u043E\u0433\u0438",
  "\u041F\u0440\u043E\u0447\u0435\u0435"
];

// src/wallets.ts
function walletConflicts(wallets, candidate) {
  if (!candidate.isActive) return [];
  return wallets.filter(
    (w) => w.id !== candidate.id && w.isActive && w.network === candidate.network && w.token === candidate.token
  ).map((w) => ({
    walletId: w.id,
    providers: w.providers.filter((p) => candidate.providers.includes(p))
  })).filter((c) => c.providers.length > 0);
}
var KNOWN_TOKEN_CONTRACTS = {
  TRC20: {
    USDT: "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t",
    USDC: "TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8",
    USDD: "TPYmHEhy5n8TCEfYGqW2rPxsghSfzghPDn",
    TUSD: "TUpMhErZL2fhh4sVNULAbNKLokS4GjC1F4"
  },
  ERC20: {
    USDT: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
    USDC: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
    DAI: "0x6B175474E89094C44Da98b954EedeAC495271d0F"
  },
  POLYGON: {
    USDT: "0xc2132D05D31c914a87C6611C10748AEb04B58e8F",
    USDC: "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359",
    "USDC.e": "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174"
  },
  ARBITRUM: {
    USDT: "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9",
    USDC: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831"
  },
  BASE: { USDC: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913" },
  OPTIMISM: {
    USDT: "0x94b008aA00579c1307B0EF2c499aD98a8ce58e58",
    USDC: "0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85"
  }
};
var STABLECOINS = /* @__PURE__ */ new Set(["USDT", "USDC", "USDC.e", "DAI", "USDD", "TUSD", "BUSD"]);
var EXPLORER_NETWORKS = ["TRC20", "ERC20", "POLYGON", "ARBITRUM", "BASE", "OPTIMISM"];
var isExplorerNetwork = (network) => EXPLORER_NETWORKS.includes(network);
function knownTokenSymbol(network, contract) {
  if (!contract) return null;
  const list = KNOWN_TOKEN_CONTRACTS[network] ?? {};
  const match = Object.entries(list).find(([, c]) => c.toLowerCase() === contract.toLowerCase());
  return match ? match[0] : null;
}
export {
  AD_PROVIDERS,
  AD_PROVIDER_LABELS,
  ALL_PERMISSIONS,
  APP_REQUEST_STATUSES,
  APP_REQUEST_STATUS_LABELS,
  APP_TRACKERS,
  BOT_COMMANDS,
  BOT_COMMAND_ACCESS_LABELS,
  BROADCAST_KINDS,
  BROADCAST_RATE_PER_SECOND,
  BUDGET_PERIODS,
  BUDGET_PERIOD_LABELS,
  BUTTON_STYLES,
  BUTTON_TYPES,
  CRYPTO_NETWORKS,
  DEFAULT_COMMISSION_LADDER,
  DEFAULT_NETWORK,
  DEFAULT_TOKEN,
  DELETE_WINDOW_HOURS,
  EXPENSE_CATEGORIES,
  EXPLORER_NETWORKS,
  KNOWN_TOKEN_CONTRACTS,
  LIVE_BOT_PROVIDERS,
  MANUAL_PROVIDERS,
  MAX_APPS_PER_REQUEST,
  MAX_BUTTONS_PER_ROW,
  MAX_BUTTON_ROWS,
  MCP_TOOLS,
  MEDIA_FIELD,
  NOTIFICATION_GROUPS,
  NOTIFICATION_KINDS,
  NOTIFICATION_LABELS,
  PAYMENT_METHODS,
  PERMISSIONS,
  PERMISSION_GROUPS,
  SCALE,
  STABLECOINS,
  SUPER_ADMIN_SLUG,
  TRANSACTION_TYPES,
  TRANSACTION_TYPE_LABELS,
  accountBudgetSchema,
  accountLabel,
  accountLeg,
  adAccountSchema,
  adCredentialSchema,
  add,
  adminUserSchema,
  allocateBody,
  apiErrorBody,
  apiSuccess,
  appRequestItem,
  appRequestSchema,
  assignAccountBody,
  botCommandsFor,
  botWebhookPath,
  broadcastDeliverySchema,
  broadcastSchema,
  budgetSummarySchema,
  budgetToAmount,
  buttonRowsSchema,
  buttonSchema,
  cabinetBalanceSchema,
  campaignDailyPointSchema,
  campaignSchema,
  canBlockCabinet,
  clientOverviewSchema,
  clientSchema,
  commissionTierSchema,
  computeAllocation,
  computeCommission,
  computeReturn,
  createAppRequestBody,
  createBroadcastBody,
  createClientBody,
  createCredentialBody,
  createDepositWalletBody,
  createRoleBody,
  createTopUpBody,
  createUserBody,
  cronToSchedule,
  dailyBudgetMicros,
  dailySpendPointSchema,
  depositWalletSchema,
  describeSchedule,
  editTopUpBody,
  editTopUpResultSchema,
  explorerAddressUrl,
  explorerUrl,
  extractHash,
  findKind,
  findMcpTool,
  findNetwork,
  fromMicros,
  hasMasterBalance,
  isAdProvider,
  isAppTracker,
  isCampaignLive,
  isExplorerNetwork,
  isInternalTransfer,
  isLiveBotProvider,
  isManualProvider,
  isNegative,
  isNotificationKind,
  isValidPermission,
  kindNeedsMedia,
  knownTokenSymbol,
  loginBody,
  loginHandle,
  miniAppContextSchema,
  minorUnitsToMicros,
  moneyString,
  notificationAllowed,
  notificationKindForTransaction,
  notificationPrefsSchema,
  percentOf,
  providerSpendSchema,
  rateForCumulative,
  returnToAccountBody,
  roleHasPermission,
  roleSchema,
  scheduleToCron,
  settingsSchema,
  shortHash,
  spendPointSchema,
  spendSeriesSchema,
  statsSchema,
  sub,
  sum,
  toInlineKeyboard,
  toMicros,
  tokensForNetwork,
  topAccountSchema,
  topUpSchema,
  topUpWithClientSchema,
  trackAccountBody,
  updateAppRequestStatusBody,
  updateClientBody,
  updateDepositWalletBody,
  updateRoleBody,
  updateTiersBody,
  updateUserBody,
  usesCabinetBalance,
  walletConflicts
};
//# sourceMappingURL=index.mjs.map